import type {
  AgentRun,
  Approval,
  Finding,
  Remediation,
  Scan,
  ScopeVersion,
} from '@ai-secure-twin/shared';

/**
 * The engagement command strip (ADR 0010, UI direction D): the CTEM loop for one engagement, from
 * scope to proof, built only from records the engagement page already holds. Pure: the same
 * records, the same strip.
 *
 * A stage is `done` when its evidence exists, `live` when work is under way in it now, `blocked`
 * when it failed or a fix was disproved, and `waiting` otherwise.
 */

export type StageState = 'done' | 'live' | 'waiting' | 'blocked';

export interface CommandStage {
  key: 'scope' | 'scan' | 'triage' | 'fix' | 'deploy' | 'proof';
  label: string;
  value: string;
  detail: string;
  state: StageState;
  /** 0–1 for a stage under way that can say how far; absent otherwise. */
  progress?: number;
}

export type EventTone = 'proof' | 'danger' | 'agent' | 'gate' | 'scan' | 'fix' | 'neutral';

export interface CommandEvent {
  at: string;
  tone: EventTone;
  title: string;
  detail: string;
}

export interface CommandInputs {
  scopeVersions: readonly ScopeVersion[];
  currentScopeVersion?: number;
  approvals: readonly Approval[];
  scans: readonly Scan[];
  findings: readonly Finding[];
  remediations: readonly Remediation[];
  agentRuns: readonly AgentRun[];
}

const IN_FLIGHT_FIX = new Set(['OPENING', 'PR_OPEN']);
const DEPLOYING = new Set(['MERGED', 'DEPLOYED', 'VERIFYING']);

const short = (id: string) => id.slice(0, 8);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function commandOf(input: CommandInputs): {
  stages: CommandStage[];
  events: CommandEvent[];
} {
  const scopeApproval = input.approvals
    .filter((a) => a.gate === 'SCOPE')
    .sort((a, b) => b.at.localeCompare(a.at))[0];
  const scansDone = input.scans.filter((s) => s.status === 'SUCCEEDED');
  const scanLive = input.scans.find((s) => s.status === 'QUEUED' || s.status === 'RUNNING');
  const scanFailed = !scanLive && input.scans.length > 0 && scansDone.length === 0;
  const decided = input.findings.filter((f) => f.status !== 'UNVERIFIED').length;
  const fixesOpen = input.remediations.filter((r) => IN_FLIGHT_FIX.has(r.status));
  const deploying = input.remediations.filter((r) => DEPLOYING.has(r.status));
  const proved = input.remediations.filter((r) => r.status === 'VERIFIED');
  const disproved = input.remediations.filter((r) => r.status === 'STILL_PRESENT');

  const scopeState: StageState =
    scopeApproval?.decision === 'APPROVED'
      ? 'done'
      : scopeApproval?.decision === 'DECLINED'
        ? 'blocked'
        : input.scopeVersions.length > 0
          ? 'live'
          : 'waiting';

  const stages: CommandStage[] = [
    {
      key: 'scope',
      label: 'Scope',
      value: input.currentScopeVersion
        ? `v${input.currentScopeVersion}`
        : input.scopeVersions.length > 0
          ? `v${input.scopeVersions.at(-1)!.version}`
          : '—',
      detail:
        scopeState === 'done'
          ? 'Approved'
          : scopeState === 'blocked'
            ? 'Declined — revise it'
            : scopeState === 'live'
              ? 'Waiting for an approver'
              : 'Define a scope',
      state: scopeState,
    },
    {
      key: 'scan',
      label: 'Scan',
      value: `${scansDone.reduce((n, s) => n + (s.findingCount ?? 0), 0)}`,
      detail: scanLive
        ? `${scanLive.status === 'QUEUED' ? 'Queued' : 'Scanning'} · scan ${short(scanLive.id)}`
        : scansDone.length > 0
          ? `${plural(scansDone.length, 'scan')} · findings raised`
          : scanFailed
            ? 'The last scan did not finish'
            : 'Not scanned yet',
      state: scanLive ? 'live' : scansDone.length > 0 ? 'done' : scanFailed ? 'blocked' : 'waiting',
    },
    {
      key: 'triage',
      label: 'Triage',
      value: input.findings.length > 0 ? `${decided}/${input.findings.length}` : '—',
      detail: input.findings.length === 0 ? 'No findings yet' : 'Findings decided',
      state:
        input.findings.length === 0
          ? 'waiting'
          : decided === input.findings.length
            ? 'done'
            : 'live',
      ...(input.findings.length > 0 ? { progress: decided / input.findings.length } : {}),
    },
    {
      key: 'fix',
      label: 'Fix',
      value: `${fixesOpen.length + deploying.length + proved.length + disproved.length}`,
      detail:
        fixesOpen.length > 0
          ? `${plural(fixesOpen.length, 'pull request')} open`
          : input.remediations.length > 0
            ? 'Pull requests merged'
            : 'No fix proposed',
      state: fixesOpen.length > 0 ? 'live' : input.remediations.length > 0 ? 'done' : 'waiting',
    },
    {
      key: 'deploy',
      label: 'Deploy',
      value: `${deploying.length}`,
      detail:
        deploying.length > 0
          ? 'Merged, waiting for the deploy and re-scan'
          : proved.length > 0
            ? 'Deployed'
            : 'Waiting for a merge',
      state:
        deploying.length > 0 ? 'live' : proved.length + disproved.length > 0 ? 'done' : 'waiting',
    },
    {
      key: 'proof',
      label: 'Proof',
      value: `${proved.length}`,
      detail:
        disproved.length > 0
          ? `${plural(disproved.length, 'fix', 'fixes')} not proved — still present`
          : proved.length > 0
            ? `${plural(proved.length, 'fix', 'fixes')} proved by re-scan`
            : 'Re-scan after deploy',
      state: disproved.length > 0 ? 'blocked' : proved.length > 0 ? 'done' : 'waiting',
    },
  ];

  // ---- Activity, newest first ----------------------------------------------------------------
  const events: CommandEvent[] = [];
  for (const a of input.approvals) {
    events.push({
      at: a.at,
      tone: a.decision === 'APPROVED' ? 'gate' : 'danger',
      title: `${a.gate[0]}${a.gate.slice(1).toLowerCase()} v${a.scopeVersion} ${a.decision === 'APPROVED' ? 'approved' : 'declined'}`,
      detail: a.scopeDigest
        ? `Digest ${a.scopeDigest.slice(0, 4)}…${a.scopeDigest.slice(-4)} recorded with the decision`
        : (a.approverEmail ?? 'Recorded'),
    });
  }
  for (const s of input.scans) {
    if (s.startedAt) {
      events.push({
        at: s.startedAt,
        tone: 'scan',
        title: `${s.kind === 'PASSIVE' ? 'Passive' : 'Active'} scan started`,
        detail: `Scope v${s.scopeVersion} · scan ${short(s.id)}`,
      });
    }
    if (s.finishedAt) {
      events.push({
        at: s.finishedAt,
        tone: s.status === 'SUCCEEDED' ? 'scan' : 'danger',
        title: s.status === 'SUCCEEDED' ? 'Scan finished' : `Scan ${s.status.toLowerCase()}`,
        detail:
          s.status === 'SUCCEEDED'
            ? plural(s.findingCount ?? 0, 'finding')
            : (s.error ?? 'No report'),
      });
    }
  }
  for (const r of input.agentRuns) {
    if (r.status !== 'SUCCEEDED' && r.status !== 'FAILED') continue;
    events.push({
      at: r.updatedAt ?? r.requestedAt,
      tone: r.status === 'SUCCEEDED' ? 'agent' : 'danger',
      title: `Engagement Lead ${TASK[r.task]}`,
      detail:
        r.status === 'SUCCEEDED' ? 'Grounded in the knowledge base' : (r.error ?? 'The run failed'),
    });
  }
  for (const r of input.remediations) {
    events.push({
      at: r.createdAt,
      tone: 'fix',
      title: `Guardian opened a fix${r.pullRequest ? ` · PR #${r.pullRequest.number}` : ''}`,
      detail: r.title,
    });
    if (r.status === 'VERIFIED' || r.status === 'STILL_PRESENT') {
      events.push({
        at: r.updatedAt,
        tone: r.status === 'VERIFIED' ? 'proof' : 'danger',
        title: r.status === 'VERIFIED' ? 'Fix proved by re-scan' : 'Re-scan found it still present',
        detail: r.verifyScanId ? `Re-scan ${short(r.verifyScanId)}` : r.title,
      });
    }
  }
  for (const f of input.findings) {
    if (!f.lastTriage) continue;
    events.push({
      at: f.lastTriage.at,
      tone: f.lastTriage.to === 'FIXED' ? 'proof' : 'neutral',
      title: `Finding marked ${f.lastTriage.to.replace('_', ' ').toLowerCase()}`,
      detail: f.title,
    });
  }
  events.sort((a, b) => b.at.localeCompare(a.at) || a.title.localeCompare(b.title));
  return { stages, events: events.slice(0, 10) };
}

const TASK: Record<AgentRun['task'], string> = {
  SUMMARISE: 'summarised the findings',
  PROPOSE_NEXT_STAGE: 'proposed the next stage',
  SUGGEST_TRIAGE: 'suggested triage',
};
